import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { ThemeService } from '../../services/theme.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  SurveyPrintService,
  SurveyPrintProgress,
} from '../../services/survey-print.service';
import { MatDialog } from '@angular/material/dialog';
import { ProgressModalComponent } from '../progress-modal/progress-modal.component';

@Component({
  selector: 'app-preview-modal',
  templateUrl: './preview-modal.component.html',
  styleUrls: ['./preview-modal.component.scss'],
})
export class PreviewModalComponent implements OnInit, OnDestroy {
  public safeUrl!: SafeResourceUrl;
  public isLoaded: boolean = false;
  public previewId: string = '';
  currentTheme: string = environment.defaultTheme;
  environment = environment;
  private themeSubscription: Subscription;
  public isPrintLoading: boolean = false;
  private progressSubscription?: Subscription;
  private iframeLoadTimeout?: ReturnType<typeof setTimeout>;
  private printTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private sanitizer: DomSanitizer,
    public dialogRef: MatDialogRef<PreviewModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private themeService: ThemeService,
    private surveyPrintService: SurveyPrintService,
    private dialog: MatDialog
  ) {
    this.currentTheme =
      this.data?.theme || this.themeService.getCurrentThemeName();

    if (this.data?.previewId) {
      this.previewId = this.data.previewId;
      this.isLoaded = true;
    }

    this.themeSubscription = this.themeService.themeChanged$.subscribe(() => {
      this.currentTheme =
        this.data?.theme || this.themeService.getCurrentThemeName();
      this.applyThemeColors();
    });
    this.applyThemeColors();
  }

  private applyThemeColors(): void {
    this.themeService
      .getThemes()
      .pipe(take(1))
      .subscribe((themesResponse) => {
        const themes = themesResponse.colors;
        const theme =
          themes[this.currentTheme] ?? themes[environment.defaultTheme];
        if (theme) {
          const headerElement = document.querySelector('.preview-header');
          if (headerElement) {
            headerElement.setAttribute(
              'style',
              `background-color: ${theme['header'] || theme['primary']}`
            );
          }
        }
      });
  }

  ngOnInit(): void {
    if (this.data && this.data.url) {
      this.isLoaded = false;
      try {
        this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.data.url
        );

      } catch (error) {
        console.error('Error processing URL:', error);
      }
    }
  }

  onIframeLoad(): void {
    if (this.iframeLoadTimeout) {
      clearTimeout(this.iframeLoadTimeout);
    }
    this.iframeLoadTimeout = setTimeout(() => {
      this.isLoaded = true;
    }, 3000);
  }

  close() {
    sessionStorage.removeItem(this.data.id);
    localStorage.removeItem(this.data.id);
    this.dialogRef.close();
  }

  ngOnDestroy() {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
    if (this.iframeLoadTimeout) {
      clearTimeout(this.iframeLoadTimeout);
    }
    if (this.printTimeout) {
      clearTimeout(this.printTimeout);
    }
  }

  printSurvey(): void {
    try {
      this.isPrintLoading = true;

      if (this.printTimeout) {
        clearTimeout(this.printTimeout);
      }
      this.printTimeout = setTimeout(() => {
        window.print();
        this.isPrintLoading = false;
      }, 300);
    } catch (error) {
      console.error('Error printing survey:', error);
      this.isPrintLoading = false;
    }
  }

  private getSurveyData(): any {
    if (this.previewId) {
      try {
        const data = sessionStorage.getItem(this.previewId);
        if (data) {
          return JSON.parse(data);
        }
      } catch (error) {
        console.error('Error loading survey data from localStorage:', error);
      }
    }
    return null;
  }

  async generatePdfSurvey(): Promise<void> {
    try {
      this.isPrintLoading = true;
      this.progressSubscription?.unsubscribe();
      const surveyData = this.getSurveyData();
      if (!surveyData) {
        throw new Error(
          'Survey data not found. Please try refreshing the preview.'
        );
      }

      const progressDialogRef = this.dialog.open(ProgressModalComponent, {
        disableClose: true,
        data: {
          title: 'Generating Survey PDF',
          progress: 0,
          message: 'Preparing survey for PDF generation...',
          currentPage: 0,
          totalPages: 0,
          enableCancel: true,
          hideWarning: true,
        },
      });

      this.progressSubscription = this.surveyPrintService.progress$.subscribe(
        (progressData: SurveyPrintProgress) => {
          if (progressDialogRef.componentInstance) {
            progressDialogRef.componentInstance.data.progress =
              progressData.progress;
            progressDialogRef.componentInstance.data.currentPage =
              progressData.currentPage;
            progressDialogRef.componentInstance.data.totalPages =
              progressData.totalPages;
            progressDialogRef.componentInstance.data.message =
              progressData.message;
          }
        }
      );

      await this.surveyPrintService.generateSurveyPDF(surveyData);

      if (progressDialogRef) {
        progressDialogRef.close();
      }

      const pdfBlob = this.surveyPrintService.getPdfBlob();
      if (pdfBlob) {
        const filename = `survey-${this.previewId}-${Date.now()}.pdf`;
        this.surveyPrintService.openPdfInNewTab(filename);
      } else {
        throw new Error('Failed to generate PDF');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      this.progressSubscription?.unsubscribe();
      this.progressSubscription = undefined;
      this.isPrintLoading = false;
    }
  }

  async downloadPdfSurvey(): Promise<void> {
    try {
      this.isPrintLoading = true;
      this.progressSubscription?.unsubscribe();

      const surveyData = this.getSurveyData();
      if (!surveyData) {
        throw new Error(
          'Survey data not found. Please try refreshing the preview.'
        );
      }

      const progressDialogRef = this.dialog.open(ProgressModalComponent, {
        disableClose: true,
        data: {
          title: 'Downloading Survey PDF',
          progress: 0,
          message: 'Preparing survey for download...',
          currentPage: 0,
          totalPages: 0,
        },
      });

      this.progressSubscription = this.surveyPrintService.progress$.subscribe(
        (progressData: SurveyPrintProgress) => {
          if (progressDialogRef.componentInstance) {
            progressDialogRef.componentInstance.data.progress =
              progressData.progress;
            progressDialogRef.componentInstance.data.currentPage =
              progressData.currentPage;
            progressDialogRef.componentInstance.data.totalPages =
              progressData.totalPages;
            progressDialogRef.componentInstance.data.message =
              progressData.message;
          }
        }
      );

      await this.surveyPrintService.generateSurveyPDF(surveyData);
      if (progressDialogRef) {
        progressDialogRef.close();
      }

      // Download the PDF
      const pdfBlob = this.surveyPrintService.getPdfBlob();
      if (pdfBlob) {
        const filename = `survey-${surveyData.title || 'survey'}-${Date.now()}.pdf`;
        this.surveyPrintService.openPdfInNewTab(filename);
      } else {
        throw new Error('Failed to generate PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      this.progressSubscription?.unsubscribe();
      this.progressSubscription = undefined;
      this.isPrintLoading = false;
    }
  }
}
